import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_danger,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import { apiPost } from "../../api";
import { reportPlayerError } from "../../utils/reportPlayerError";
import UnlockPanel from "../UnlockPanel";
import { buildLoginUrl } from "../loginRedirectUrl";

// Inline Video.js player for the rebranded /webinars/:slug detail page.
// The player renders only when `webinar.video_recording` is present (which the
// server gates on is_registered + is_done + Video record exists). When it is
// absent but a recording DOES exist, an explanatory panel is rendered instead
// of nothing, driven by `webinar.recording_state` — a user who saw the
// "Rekaman" chip on the card must never land on a silently empty page.
//
// Lazy-loaded — Video.js + plugins are dynamic-imported inside useEffect
// so they don't ship in the main bundle and don't break SSR. Plugin order
// is load-bearing: videojs-contrib-quality-levels MUST register before
// videojs-hls-quality-selector, mirroring the legacy Stimulus controller
// at app/javascript/controllers/videos/player_controller.js:6-7.
//
// First-play hook fires `apiPost(log_watch_url)` exactly once per mount
// (firedLogRef guards). Idempotent at the service layer (one
// WatchActivityLog row per video × user × day) so accidental double-fires
// are harmless, but we still guard for cleanliness.
function WebinarRecording({ webinar, authRebrand = false }) {
  const recording = webinar?.video_recording;
  // Hooks must be unconditional — set up state first, then render-bail below.
  const videoElRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const firedLogRef = React.useRef(false);
  const [errorState, setErrorState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [retryNonce, setRetryNonce] = React.useState(0);

  React.useEffect(() => {
    if (!recording || !videoElRef.current) return undefined;
    // Only fail-safe SSR guard — useEffect doesn't run server-side, but
    // belt-and-suspenders against any future test harness that might.
    if (typeof window === "undefined") return undefined;

    let cancelled = false;
    let player;

    setErrorState(null);
    setLoading(true);
    firedLogRef.current = false;

    (async () => {
      try {
        // Order matters: quality-levels registers a plugin that
        // hls-quality-selector then extends. CSS side-effect import is
        // also deferred so the ~50KB stylesheet doesn't block first paint
        // for users without a recording.
        const [{ default: videojs }] = await Promise.all([
          import("video.js"),
          import("video.js/dist/video-js.css"),
          import("videojs-contrib-quality-levels"),
          import("videojs-hls-quality-selector"),
        ]);
        if (cancelled || !videoElRef.current) return;

        player = videojs(videoElRef.current, {
          controls: true,
          fluid: true,
          playbackRates: [0.5, 1, 1.5, 2],
          userActions: { doubleClick: false },
          controlBar: { pictureInPictureToggle: false },
          poster: recording.poster_url || undefined,
        });
        playerRef.current = player;

        player.ready(() => {
          if (cancelled) return;
          try {
            player.bigPlayButton.hide();
          } catch (_) {
            /* harmless */
          }

          installSkipButtons(videojs, player);

          if (recording.source_kind === "hls") {
            try {
              player.hlsQualitySelector({ displayCurrentQuality: true });
            } catch (e) {
              // Some browsers (iOS Safari w/ native HLS) don't expose the
              // quality-levels API — selector is optional, never fatal.
              console.warn(
                "[WebinarRecording] quality selector unavailable",
                e,
              );
            }
          }

          player.src({
            src: recording.source_url,
            type: typeFor(recording.source_kind),
          });

          player.on("play", handleFirstPlay);
          player.on("error", handleError);
          setLoading(false);
        });
      } catch (e) {
        if (!cancelled) {
          console.error("[WebinarRecording] init failed", e);
          setErrorState({ message: e?.message || "init_failed" });
          setLoading(false);
        }
      }
    })();

    function handleFirstPlay() {
      if (firedLogRef.current) return;
      firedLogRef.current = true;
      // Fire-and-forget — never block playback on the log POST.
      apiPost(recording.log_watch_url, {}).catch((err) => {
        console.warn("[WebinarRecording] log_watch failed", err);
      });
    }

    function handleError() {
      const err = (player && player.error()) || { message: "playback_error" };
      console.warn("[WebinarRecording] player error", err);
      setErrorState(err);
      reportPlayerError({
        video_id: recording?.id,
        source_url: player && player.currentSrc(),
        error_code: err && err.code != null ? String(err.code) : "unknown",
        error_message: err && err.message ? err.message : "",
        position_seconds: (player && player.currentTime()) || 0,
      });
    }

    return () => {
      cancelled = true;
      if (playerRef.current && !playerRef.current.isDisposed_) {
        try {
          playerRef.current.dispose();
        } catch (_) {
          /* harmless */
        }
      }
      playerRef.current = null;
    };
  }, [recording?.id, recording?.source_url, retryNonce]);

  // No player payload. Rather than vanishing (which reads as a broken page
  // when the catalog card advertised a "Rekaman" chip), say WHY. `state` is
  // server-computed — see Homepage::WebinarPresenter#recording_state.
  if (!recording || !recording.source_url) {
    const state = webinar?.recording_state;
    if (!state || state === "none") return null;

    return (
      <Section id="webinar-recording" aria-labelledby="webinar-recording-title">
        <Inner>
          <Title id="webinar-recording-title">Rekaman Webinar</Title>
          {state === "upcoming" && (
            <UnlockPanel icon="🎬" title="Rekaman belum tersedia">
              <p>
                Webinar ini belum selesai. Rekaman akan terbit di halaman ini
                setelah sesi berakhir dan diproses — pastikan Anda terdaftar
                agar bisa menontonnya.
              </p>
            </UnlockPanel>
          )}
          {state === "login_required" && (
            <UnlockPanel
              title="Masuk untuk mengecek akses rekaman"
              actions={[
                {
                  label: "Masuk",
                  href: buildLoginUrl(webinar?.slug, authRebrand),
                },
              ]}
            >
              <p>
                Webinar ini memiliki rekaman. Rekaman hanya dapat ditonton oleh
                peserta yang terdaftar pada webinar tersebut, jadi silakan masuk
                terlebih dahulu untuk memeriksa akses Anda.
              </p>
            </UnlockPanel>
          )}
          {state === "not_registered" && (
            <UnlockPanel
              title="Rekaman khusus peserta terdaftar"
              actions={[
                {
                  label: "Lihat Webinar Lainnya",
                  href: "/webinars",
                  variant: "secondary",
                },
              ]}
            >
              <p>
                Webinar ini sudah selesai dan rekamannya tersedia, tetapi hanya
                untuk peserta yang mendaftar sebelum sesi berlangsung. Karena
                Anda tidak terdaftar pada webinar ini, rekamannya tidak dapat
                diputar — ini bukan kesalahan sistem.
              </p>
              <p>
                Agar tidak terlewat lagi, daftarkan diri Anda pada webinar yang
                akan datang: rekamannya otomatis bisa Anda tonton di sini
                setelah sesi selesai.
              </p>
            </UnlockPanel>
          )}
        </Inner>
      </Section>
    );
  }

  if (errorState) {
    return (
      <Section id="webinar-recording" aria-labelledby="webinar-recording-title">
        <Inner>
          <Title id="webinar-recording-title">Rekaman Webinar</Title>
          <ErrorPanel role="alert">
            <ErrorIcon aria-hidden="true">⚠️</ErrorIcon>
            <div>
              <strong>Rekaman tidak dapat dimuat saat ini.</strong>
              <p>
                Tautan rekaman mungkin telah kedaluwarsa atau koneksi terputus.
                Silakan coba lagi.
              </p>
              <RetryButton
                type="button"
                onClick={() => {
                  setErrorState(null);
                  setLoading(true);
                  setRetryNonce((n) => n + 1);
                }}
              >
                Coba Lagi
              </RetryButton>
            </div>
          </ErrorPanel>
        </Inner>
      </Section>
    );
  }

  return (
    <Section id="webinar-recording" aria-labelledby="webinar-recording-title">
      <Inner>
        <Title id="webinar-recording-title">Rekaman Webinar</Title>
        <Subtitle>
          Tonton kembali sesi webinar yang telah Anda ikuti. Putar di mana saja,
          dengan kontrol kecepatan dan pilihan kualitas.
        </Subtitle>
        <PlayerFrame $loading={loading}>
          {loading && (
            <PlayerSkeleton aria-hidden="true">
              <SkeletonShimmer />
            </PlayerSkeleton>
          )}
          <video
            ref={videoElRef}
            className="video-js vjs-big-play-centered vjs-default-skin"
            playsInline
            preload="metadata"
            data-testid="webinar-recording-player"
          />
        </PlayerFrame>
      </Inner>
    </Section>
  );
}

export default React.memo(WebinarRecording);

// ---- helpers ---------------------------------------------------------------

function typeFor(kind) {
  switch (kind) {
    case "hls":
      return "application/x-mpegURL";
    case "webm":
      return "video/webm";
    default:
      return "video/mp4";
  }
}

// Mirror legacy +10s / -10s skip controls in the control bar. Built lazily
// using videojs.getComponent so the registration only happens once Video.js
// is loaded.
function installSkipButtons(videojs, player) {
  try {
    const Button = videojs.getComponent("Button");
    if (!Button) return;

    class SkipBackButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.controlText("Mundur 10 detik");
        this.addClass("vjs-icon-replay-10");
      }
      handleClick() {
        const t = player.currentTime();
        player.currentTime(Math.max(0, t - 10));
      }
    }
    class SkipForwardButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.controlText("Maju 10 detik");
        this.addClass("vjs-icon-forward-10");
      }
      handleClick() {
        const dur = player.duration() || 0;
        const t = player.currentTime();
        player.currentTime(Math.min(dur, t + 10));
      }
    }

    const controlBar = player.getChild("ControlBar");
    if (!controlBar) return;
    controlBar.addChild(new SkipBackButton(player), {}, 1);
    controlBar.addChild(new SkipForwardButton(player), {}, 2);
  } catch (e) {
    console.warn("[WebinarRecording] skip buttons unavailable", e);
  }
}

// ---- styles ----------------------------------------------------------------

const Section = styled.section`
  background: #ffffff;
  padding: 56px 0 32px;

  @media ${narrowScreenMediaQuery} {
    padding: 32px 0 20px;
  }

  [data-theme="dark"] & {
    background: #0f172a;
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 6px;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 18px;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const PlayerFrame = styled.div`
  position: relative;
  background: #000;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  border: 1px solid #f3f4f6;

  /* Video.js fills the frame; aspect-ratio container provides reserved space
   * so the page doesn't reflow when the player CSS finishes loading.
   */
  & .video-js {
    width: 100%;
    height: 100%;
  }

  [data-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  }
`;

const PlayerSkeleton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1f2937, #0f172a);
  z-index: 1;
`;

const SkeletonShimmer = styled.div`
  width: 60%;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.18) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

const ErrorPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 24px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 16px;
  color: #991b1b;
  line-height: 1.5;

  strong {
    display: block;
    font-size: 16px;
    margin-bottom: 4px;
    color: ${brand_danger};
  }
  p {
    margin: 0 0 12px;
    font-size: 14px;
  }

  [data-theme="dark"] & {
    background: rgba(220, 38, 38, 0.08);
    border-color: rgba(220, 38, 38, 0.24);
    color: #fca5a5;
    strong {
      color: #fca5a5;
    }
  }
`;

const ErrorIcon = styled.span`
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
`;

const RetryButton = styled.button`
  display: inline-block;
  padding: 8px 18px;
  border-radius: 10px;
  border: none;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  &:hover {
    filter: brightness(0.95);
  }
  &:active {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }
`;
